import { useQuery } from '@apollo/client/react'

import { PlanSelector } from '@buffer-mono/app-shell'
import { OBPlanIdValues } from '@buffer-mono/app-shell/src/components/PlanSelector/types'
import {
  EmptyState,
  CriticalIcon,
  LockIcon,
  Skeleton,
  Link,
} from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'

import {
  BillingEmail,
  BillingEmail_BillingDetails,
} from './components/BillingEmail'
import {
  BillingInformation,
  BillingInformation_WebBillingDetails,
} from './components/BillingInformation'
import { Invoices, Invoices_BillingDetails } from './components/Invoices'
import { LegacyPlanBilling } from './components/LegacyPlanBilling'
import {
  MobileBilling,
  MobileBilling_MobileBillingDetails,
  MobileBilling_Organization,
} from './components/MobileBilling'
import {
  PaymentDetails,
  PaymentDetails_WebBillingDetails,
} from './components/PaymentDetails'
import { PlanCardsVertical_WebBillingDetails } from './components/PlanCardsVertical'
import {
  OrganizationDataForBilling_Organization,
  PlanContent,
  PlanContent_WebBillingDetails,
} from './components/PlanContent'
import { useBillingUpdatedListener } from './hooks/useBillingUpdatedListener'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'

import styles from './Billing.module.css'

import type { JSX } from 'react'

export const OrganizationPrivileges_Organization = graphql(/* GraphQL */ `
  fragment OrganizationPrivileges_Organization on Organization {
    canAddPaymentDetails
    allowedActions
  }
`)

export const OrganizationTracking_Organization = graphql(/* GraphQL */ `
  fragment OrganizationTracking_Organization on Organization {
    id
    commonTrackingProperties {
      atEventBillingState
      atEventBillingPlan
      atEventBillingCycle
      atEventBillingGateway
      atEventIsNewBuffer
      atEventChannelQty
    }
  }
`)

// Only reads `billing.__typename` (plus the MPBilling gateway) to detect
// MPBilling before firing `BillingPageQuery` (which errors for non-OneBuffer
// orgs). All other billing fields are consumed from `billing(input:)` via child
// fragments below.
export const AccountForBillingQuery = graphql(
  /* GraphQL */ `
    query Account {
      account {
        id
        isImpersonation
        currentOrganization {
          id
          billing {
            __typename
            ... on MPBilling {
              gateway {
                gatewayType
              }
            }
          }
          ...MobileBilling_Organization
          ...OrganizationDataForBilling_Organization
          ...OrganizationPrivileges_Organization
          ...OrganizationTracking_Organization
        }
      }
    }
  `,
  [
    MobileBilling_Organization,
    OrganizationDataForBilling_Organization,
    OrganizationPrivileges_Organization,
    OrganizationTracking_Organization,
  ],
)

// Single parent query over the new `billing(input:)` schema. Child components
// contribute fragments on WebBillingDetails / MobileBillingDetails /
// BillingDetails; we also select the fields needed for the page's own routing
// logic (plan id, gateway, previousGateway, automatic-tax flag) at the top
// level so they aren't hidden behind fragment masking.
// Skipped for MPBilling users — the backend resolver errors for legacy MPBilling
// orgs, so we detect that typename from AccountForBillingQuery first.
export const BillingPageQuery = graphql(
  /* GraphQL */ `
    query BillingPage($input: BillingInput!) {
      billing(input: $input) {
        __typename
        subscription {
          plan {
            id
          }
        }
        gateway {
          type
        }
        previousGateway {
          type
        }
        ...BillingEmail_BillingDetails
        ...Invoices_BillingDetails
        ... on WebBillingDetails {
          taxDetails {
            isAutomaticTaxEnabled
          }
          ...PlanContent_WebBillingDetails
          ...PlanCardsVertical_WebBillingDetails
          ...PaymentDetails_WebBillingDetails
          ...BillingInformation_WebBillingDetails
        }
        ... on MobileBillingDetails {
          ...MobileBilling_MobileBillingDetails
        }
      }
    }
  `,
  [
    BillingEmail_BillingDetails,
    Invoices_BillingDetails,
    PlanContent_WebBillingDetails,
    PlanCardsVertical_WebBillingDetails,
    PaymentDetails_WebBillingDetails,
    BillingInformation_WebBillingDetails,
    MobileBilling_MobileBillingDetails,
  ],
)

const PAGE_TITLE = 'Billing'

export const Billing = (): JSX.Element => {
  const organizationId = useOrganizationId()
  const { data, loading, error, refetch } = useQuery(AccountForBillingQuery)

  // MPBilling orgs make `billing(input:)` error, so skip BillingPageQuery
  // entirely for them and fall back to the MPBilling branch that doesn't need
  // it. We only know after AccountForBillingQuery resolves.
  const isMPBilling =
    data?.account?.currentOrganization?.billing?.__typename === 'MPBilling'

  const {
    data: billingPageData,
    loading: billingPageLoading,
    error: billingPageError,
    refetch: refetchBillingPage,
  } = useQuery(BillingPageQuery, {
    variables: { input: { organizationId: organizationId ?? '' } },
    skip: !organizationId || loading || isMPBilling,
  })

  // `refetchBillingPage` is skipped for MPBilling orgs — Apollo's refetch
  // bypasses the `skip` option, so we also gate it here to avoid a known-to-
  // error round-trip for legacy orgs.
  useBillingUpdatedListener({
    refetch,
    refetchBillingPage,
    skipBillingPage: isMPBilling,
  })

  const orgPrivileges = data?.account?.currentOrganization
    ? readFragment(
        OrganizationPrivileges_Organization,
        data.account.currentOrganization,
      )
    : null

  const organizationData = data?.account?.currentOrganization ?? null

  const trackingData = data?.account?.currentOrganization
    ? readFragment(
        OrganizationTracking_Organization,
        data.account.currentOrganization,
      )
    : null

  const billingPage = billingPageData?.billing ?? null

  // Automatic tax only applies to web billing (Stripe). MobileBillingDetails
  // and MPBilling both fall back to `false` — matching prior behaviour.
  const isAutomaticTaxEnabled =
    billingPage?.__typename === 'WebBillingDetails'
      ? billingPage.taxDetails.isAutomaticTaxEnabled
      : false

  const userData = trackingData
    ? {
        isImpersonation: data?.account?.isImpersonation ?? false,
        currentOrganization: {
          id: trackingData.id,
          commonTrackingProperties: trackingData.commonTrackingProperties,
          billing: { isAutomaticTaxEnabled },
        },
      }
    : undefined

  const isFreePlan = billingPage?.subscription.plan.id === OBPlanIdValues.free

  // BillingPageQuery is skipped for MPBilling users, so ignore its error in
  // that branch (the backend errors on `billing(input:)` for MPBilling).
  if (error || (billingPageError && !isMPBilling)) {
    return (
      <NewSettingsPageLayout title={PAGE_TITLE}>
        <EmptyState variant="critical">
          <EmptyState.Icon>
            <CriticalIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>
            Failed to load billing information
          </EmptyState.Heading>
          <EmptyState.Description>
            Something went wrong. Please try again later.
          </EmptyState.Description>
        </EmptyState>
      </NewSettingsPageLayout>
    )
  }

  // Wait for BillingPageQuery unless we're on MPBilling (query skipped).
  const waitingForBillingPage =
    !isMPBilling && (billingPageLoading || !billingPage)

  if (loading || waitingForBillingPage) {
    return (
      <NewSettingsPageLayout title={PAGE_TITLE}>
        <Skeleton width="100%" height="250px" />
        <Skeleton width="100%" height="75px" />
        <Skeleton width="100%" height="150px" />
        <Skeleton width="100%" height="150px" />
      </NewSettingsPageLayout>
    )
  }

  if (
    orgPrivileges &&
    !orgPrivileges.allowedActions.includes('manageBilling')
  ) {
    return (
      <NewSettingsPageLayout title="Billing">
        <EmptyState>
          <EmptyState.Icon>
            <LockIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>
            Contact an admin to manage billing
          </EmptyState.Heading>
          <EmptyState.Description>
            Need help?{' '}
            <Link
              as="button"
              onClick={(): void => {
                window.Brainfish?.Widgets?.open()
              }}
            >
              Talk to our support team
            </Link>
          </EmptyState.Description>
        </EmptyState>
      </NewSettingsPageLayout>
    )
  }

  const isWebBillingDetails = billingPage?.__typename === 'WebBillingDetails'
  const isMobileBillingDetails =
    billingPage?.__typename === 'MobileBillingDetails'

  if (isMPBilling) {
    // `billing(input:)` errors for MPBilling orgs, so we can't render a
    // billing-details section; offer advocacy and the Stripe portal instead.
    const legacyBilling = data?.account?.currentOrganization?.billing
    const isStripeGateway =
      legacyBilling?.__typename === 'MPBilling' &&
      legacyBilling.gateway.gatewayType === 'stripe'

    return (
      <NewSettingsPageLayout title={PAGE_TITLE}>
        <LegacyPlanBilling canOpenBillingPortal={isStripeGateway} />

        <Invoices />
      </NewSettingsPageLayout>
    )
  }

  if (billingPage?.gateway.type === 'revenuecat') {
    return (
      <NewSettingsPageLayout title={PAGE_TITLE}>
        {isMobileBillingDetails && organizationData && (
          <MobileBilling
            organization={organizationData}
            mobileBilling={billingPage}
          />
        )}

        {/* If the user's previous billing gateway was Stripe, let's fetch the old invoices */}
        {billingPage.previousGateway?.type === 'stripe' && (
          <Invoices billingDetails={billingPage} />
        )}
      </NewSettingsPageLayout>
    )
  }

  if (isFreePlan) {
    return (
      <NewSettingsPageLayout
        title="Flexible Pricing for Everyone"
        className={styles.planSelector}
      >
        <PlanSelector
          cta="publish-settings-billing-noClick-1"
          ctaButton="noClick"
          upgradePathName="settingsBilling"
          user={userData}
        />

        {isWebBillingDetails && (
          <PaymentDetails
            webBilling={billingPage}
            canAddPaymentDetails={orgPrivileges?.canAddPaymentDetails ?? false}
          />
        )}

        {billingPage && <Invoices billingDetails={billingPage} />}
      </NewSettingsPageLayout>
    )
  }

  return (
    <NewSettingsPageLayout title={PAGE_TITLE}>
      {isWebBillingDetails && (
        <>
          <PlanContent
            webBilling={billingPage}
            organizationData={organizationData}
          />
          <PaymentDetails
            webBilling={billingPage}
            canAddPaymentDetails={orgPrivileges?.canAddPaymentDetails ?? false}
          />
          <BillingInformation webBilling={billingPage} />
        </>
      )}

      {billingPage && <BillingEmail billingDetails={billingPage} />}

      {billingPage && <Invoices billingDetails={billingPage} />}
    </NewSettingsPageLayout>
  )
}

export default Billing
